// Accès aux données d'Arceau Game (D-161, D-162). Comme drills-api.ts : on demande, la base ne renvoie que ce que le
// compte a le droit de voir (le staff du club lit les parties préparées et les parties jouées ; personne d'autre, rien).
// Une partie jouée ne garde que sa durée et le résultat par équipe, sans aucun nom (D-103) : la base retire le reste.
import { supabase } from './supabase';

const db = () => {
  if (!supabase) throw new Error('Base non configurée');
  return supabase;
};
async function must<T = unknown>(q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>): Promise<T> {
  const { data, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return data as T;
}

export interface Game {
  id: string; club_id: string; team_id: string | null; kind: 'parcours' | 'sablier'; title: string; config: Record<string, unknown>;
  exercise_id: string | null; created_by: string | null; created_at: string; updated_at: string;
}
const COLS = 'id, club_id, team_id, kind, title, config, exercise_id, created_by, created_at, updated_at';

/** Les parties préparées du club (staff). */
export const clubGames = async (clubId: string) =>
  (await must<Game[]>(db().from('games').select(COLS).eq('club_id', clubId).is('archived_at', null).order('updated_at', { ascending: false }))) ?? [];
export const gameById = async (id: string) => ((await must<Game[]>(db().from('games').select(COLS).eq('id', id).is('archived_at', null))) ?? [])[0] ?? null;

export async function saveGame(g: { id?: string | null; club_id: string; team_id: string | null; title: string; config: unknown; exercise_id?: string | null; kind?: Game['kind'] }): Promise<string> {
  const row = { title: g.title.slice(0, 80), config: g.config, team_id: g.team_id, exercise_id: g.exercise_id ?? null, kind: g.kind ?? 'parcours' };
  const saved = g.id
    ? await must<{ id: string }[]>(db().from('games').update(row).eq('id', g.id).select('id'))
    : [await must<{ id: string }>(db().from('games').insert({ ...row, club_id: g.club_id }).select('id').single())];
  if (!saved[0]) throw new Error('réservé à son auteur');
  return saved[0].id;
}
/** Supprimer un jeu, ou le remettre (« Annuler », `on` faux) : l'auteur ou le bureau ; refusé sinon (D-178). */
export async function archiveGame(id: string, on = true): Promise<void> {
  const r = await must<{ id: string }[]>(db().from('games').update({ archived_at: on ? new Date().toISOString() : null }).eq('id', id).select('id'));
  if (!r?.length) throw new Error('réservé à son auteur ou au bureau');
}

/** Une partie jouée : sa durée et le résultat par équipe (chasuble, place, case ou sable, défis), jamais un nom. */
export const noteGameRun = (r: { game_id: string | null; club_id: string; team_id: string | null; mode: 'teams' | 'solo'; duration_s: number; result: unknown[]; kind?: Game['kind'] }) =>
  must(db().from('game_runs').insert({ ...r, kind: r.kind ?? 'parcours' }));

// -----------------------------------------------------------------------------------------------------------------
// « Mes paquets de cartes » (D-167) : comme les jeux, au club, avec un auteur ; le staff lit, l'auteur ou le bureau modifie.
// -----------------------------------------------------------------------------------------------------------------
export interface GameDeck { id: string; club_id: string; title: string; cards: unknown[]; created_by: string | null; updated_at: string }
const DCOLS = 'id, club_id, title, cards, created_by, updated_at';
export const clubDecks = async (clubId: string) =>
  (await must<GameDeck[]>(db().from('game_decks').select(DCOLS).eq('club_id', clubId).is('archived_at', null).order('updated_at', { ascending: false }))) ?? [];
export async function saveDeck(d: { id?: string | null; club_id: string; title: string; cards: unknown[] }): Promise<string> {
  const row = { title: d.title.replace(/\s+/g, ' ').trim().slice(0, 60) || 'Mon paquet', cards: d.cards };
  const saved = d.id
    ? await must<{ id: string }[]>(db().from('game_decks').update(row).eq('id', d.id).select('id'))
    : [await must<{ id: string }>(db().from('game_decks').insert({ ...row, club_id: d.club_id }).select('id').single())];
  if (!saved[0]) throw new Error('réservé à son auteur');
  return saved[0].id;
}
/** Supprimer un paquet, ou le remettre (« Annuler », `on` faux) : l'auteur ou le bureau ; refusé sinon (D-178). */
export async function archiveDeck(id: string, on = true): Promise<void> {
  const r = await must<{ id: string }[]>(db().from('game_decks').update({ archived_at: on ? new Date().toISOString() : null }).eq('id', id).select('id'));
  if (!r?.length) throw new Error('réservé à son auteur ou au bureau');
}

// -----------------------------------------------------------------------------------------------------------------
// Les réglages par défaut du coach (D-167) : les siens, repris à la prochaine préparation.
// -----------------------------------------------------------------------------------------------------------------
export async function myGamePrefs(): Promise<Record<string, unknown>> {
  const r = await must<{ prefs: Record<string, unknown> }[]>(db().from('game_prefs').select('prefs').limit(1)).catch(() => []);
  return r[0]?.prefs ?? {};
}
export const saveGamePrefs = (prefs: Record<string, unknown>) => must(db().from('game_prefs').upsert({ prefs, updated_at: new Date().toISOString() }));
